// View: /volume — объём производства за период.
//
// Источник: GET /api/stats/volume?from=&to=&project_id=&resource_id=
// Считаем cost_rub × Δwork_pct из audit_log (см. volume_calculator.py).
//
// Разбивка по флагу is_rework: задачи-корректировки (миграция 029) идут
// отдельной строкой в таблице с подсветкой; в сводке три total — общий,
// основной, переделки.

import { api } from '../api.js';

const RUB = new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 0 });

function ymd(d) {
  return d.toISOString().slice(0, 10);
}

function defaultRange() {
  const now = new Date();
  const from = new Date(now.getFullYear(), now.getMonth(), 1);
  return { from: ymd(from), to: ymd(now) };
}

let _state = {
  ...defaultRange(),
  project_id: '',
  resource_id: '',
};

export async function initVolume(container) {
  container.innerHTML = `
    <div class="volume-root">
      <div class="volume-header">
        <h2>📈 Объём производства за период</h2>
        <div class="volume-hint">
          Считаем по audit_log: <em>(новый work_pct − старый) × cost_rub задачи</em>.
          Переделки (флаг is_rework) выделены отдельно — они оплачиваются, но съедают буфер CCPM.
        </div>
      </div>

      <form class="volume-filters" id="volume-filters">
        <label>С
          <input type="date" name="from" value="${_state.from}" required />
        </label>
        <label>По
          <input type="date" name="to" value="${_state.to}" required />
        </label>
        <label>Проект
          <select name="project_id"><option value="">— все —</option></select>
        </label>
        <label>Сотрудник
          <select name="resource_id"><option value="">— все —</option></select>
        </label>
        <button type="submit" class="volume-apply">Применить</button>
      </form>

      <div id="volume-summary" class="volume-summary"></div>
      <div id="volume-table" class="volume-table-wrap">
        <div class="loading-spinner">Загрузка…</div>
      </div>
    </div>
  `;

  const form = container.querySelector('#volume-filters');
  await fillFilters(form);
  await load(container);

  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    const fd = new FormData(form);
    _state = {
      from: fd.get('from'),
      to: fd.get('to'),
      project_id: fd.get('project_id') || '',
      resource_id: fd.get('resource_id') || '',
    };
    await load(container);
  });
}

async function fillFilters(form) {
  const projSel = form.querySelector('select[name="project_id"]');
  const resSel = form.querySelector('select[name="resource_id"]');

  const settle = (p) => p.then(v => ({ ok: true, v })).catch(() => ({ ok: false, v: [] }));
  const [proj, res] = await Promise.all([
    settle(api.projects()),
    settle(api.resources()),
  ]);

  for (const p of (proj.v || [])) {
    const opt = document.createElement('option');
    opt.value = String(p.id);
    opt.textContent = `[${p.id}] ${p.name}`;
    if (String(_state.project_id) === String(p.id)) opt.selected = true;
    projSel.appendChild(opt);
  }
  for (const r of (res.v || [])) {
    const opt = document.createElement('option');
    opt.value = String(r.id);
    opt.textContent = r.name;
    if (String(_state.resource_id) === String(r.id)) opt.selected = true;
    resSel.appendChild(opt);
  }
}

async function load(container) {
  const summarySlot = container.querySelector('#volume-summary');
  const tableSlot = container.querySelector('#volume-table');
  tableSlot.innerHTML = `<div class="loading-spinner">Загрузка…</div>`;
  summarySlot.innerHTML = '';

  try {
    const data = await api.getVolume({
      from: _state.from,
      to: _state.to,
      project_id: _state.project_id || undefined,
      resource_id: _state.resource_id || undefined,
    });
    summarySlot.innerHTML = renderSummary(data);
    tableSlot.innerHTML = renderTable(data.rows || []);
  } catch (e) {
    tableSlot.innerHTML = `<div class="dash-error">Ошибка: ${esc(e.detail || e.message)}</div>`;
  }
}

function renderSummary(d) {
  return `
    <div class="volume-cards">
      <div class="volume-card volume-card-total">
        <div class="volume-card-key">Всего за период</div>
        <div class="volume-card-value">${RUB.format(d.total_rub)} ₽</div>
        <div class="volume-card-hint">${d.from} … ${d.to}</div>
      </div>
      <div class="volume-card volume-card-main">
        <div class="volume-card-key">Основной объём</div>
        <div class="volume-card-value">${RUB.format(d.total_main_rub)} ₽</div>
      </div>
      <div class="volume-card volume-card-rework">
        <div class="volume-card-key">Переделки (брак)</div>
        <div class="volume-card-value">${RUB.format(d.total_rework_rub)} ₽</div>
        <div class="volume-card-hint">съедает буфер CCPM</div>
      </div>
    </div>
  `;
}

function renderTable(rows) {
  if (!rows.length) {
    return `<div class="volume-empty">Нет изменений work_pct за период по выбранным фильтрам.</div>`;
  }
  const body = rows.map(r => `
    <tr class="${r.is_rework ? 'volume-row-rework' : ''}">
      <td>${esc(r.event_date)}</td>
      <td>${r.project_id ? `<a data-route="/gantt/${r.project_id}">${esc(r.project_name || `#${r.project_id}`)}</a>` : '—'}</td>
      <td>${esc(r.resource_name || '—')}</td>
      <td>${r.is_rework
        ? '<span class="volume-tag volume-tag-rework" title="Переделка / брак">переделка</span>'
        : '<span class="volume-tag volume-tag-main">основной</span>'}</td>
      <td class="volume-num ${Number(r.delta_volume_rub) < 0 ? 'volume-neg' : ''}">${RUB.format(r.delta_volume_rub)} ₽</td>
    </tr>
  `).join('');
  return `
    <table class="volume-table">
      <thead>
        <tr>
          <th>Дата</th>
          <th>Проект</th>
          <th>Сотрудник</th>
          <th>Тип</th>
          <th class="volume-num">Объём за день</th>
        </tr>
      </thead>
      <tbody>${body}</tbody>
    </table>
  `;
}

function esc(s) {
  return String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}
